import { ThemeEvents } from '@theme/events';
import { getDrawerSectionRenderUrl } from '@theme/section-renderer';

const INIT_FLAG = '__bbCartDrawerHotAddBound';
const FETCH_TIMEOUT_MS = 15000;

/**
 * @returns {HTMLElement | null}
 */
function getCartDrawerComponent() {
  const scoped = document.querySelector('[data-bb-custom-header-cart] cart-drawer-component');
  if (scoped instanceof HTMLElement) return scoped;

  const fallback = document.querySelector('cart-drawer-component');
  return fallback instanceof HTMLElement ? fallback : null;
}

/**
 * @returns {string | null}
 */
function getDrawerSectionId() {
  const cartItems = getDrawerCartItems();
  const section = cartItems?.closest('.shopify-section');

  if (section?.id) {
    return section.id.replace(/^shopify-section-/, '');
  }

  return cartItems?.dataset?.sectionId ?? null;
}

/**
 * @returns {HTMLElement | null}
 */
function getDrawerCartItems() {
  const drawer = getCartDrawerComponent();
  const el = drawer?.querySelector('cart-items-component[data-drawer]');
  return el instanceof HTMLElement ? el : null;
}

/**
 * @param {boolean} isUpdating
 */
function setDrawerUpdating(isUpdating) {
  const drawer = getCartDrawerComponent();
  const dialog = drawer?.querySelector('.cart-drawer__dialog');

  dialog?.classList.toggle('cart-drawer--hot-add-busy', isUpdating);

  if (drawer instanceof HTMLElement) {
    if (isUpdating) {
      drawer.setAttribute('data-drawer-updating', '');
    } else {
      drawer.removeAttribute('data-drawer-updating');
    }
  }
}

/**
 * @param {string} url
 * @param {RequestInit} [options]
 * @returns {Promise<Response>}
 */
function fetchWithTimeout(url, options) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

  return fetch(url, { ...options, signal: controller.signal }).finally(() => {
    clearTimeout(timeoutId);
  });
}

/**
 * @param {HTMLFormElement} form
 */
async function addHotProductToCart(form) {
  const cartItems = getDrawerCartItems();
  const sectionId = getDrawerSectionId();
  const formData = new FormData(form);

  if (sectionId) {
    formData.set('sections', sectionId);
    formData.set('sections_url', getDrawerSectionRenderUrl().pathname);
  }

  setDrawerUpdating(true);

  try {
    const addResponse = await fetchWithTimeout(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData,
    });

    const addData = await addResponse.json();
    if (!addResponse.ok || addData.status) {
      throw new Error(addData.description || addData.message || 'Failed to add item to cart.');
    }

    const cartItemsEl = getDrawerCartItems();

    if (sectionId && typeof cartItemsEl?.updateDrawerFromAddResponse === 'function') {
      await cartItemsEl.updateDrawerFromAddResponse(addData);
    } else if (!sectionId) {
      throw new Error('Mini cart section id is missing.');
    } else {
      throw new Error('Mini cart update handler is not available.');
    }

    const cartResponse = await fetchWithTimeout(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
    });

    const optimisticCount = typeof addData.item_count === 'number' ? addData.item_count : 1;
    const cart = cartResponse.ok ? await cartResponse.json() : { item_count: optimisticCount };
    const itemCount = typeof cart.item_count === 'number' ? cart.item_count : optimisticCount;

    document.dispatchEvent(
      new CustomEvent(ThemeEvents.cartUpdate, {
        bubbles: true,
        detail: {
          resource: { ...cart, item_count: itemCount },
          sourceId: 'cart-drawer-hot-add',
          data: {
            source: 'cart-drawer-hot-add',
            itemCount,
            skipDrawerUpdate: true,
          },
        },
      })
    );
  } finally {
    setDrawerUpdating(false);
  }
}

function bindHotAddButtons() {
  document.addEventListener(
    'click',
    async (event) => {
      const button = event.target.closest('.cart-drawer__hot-add-button');
      if (!(button instanceof HTMLButtonElement)) return;

      const form = button.closest('.cart-drawer__hot-add-form');
      if (!(form instanceof HTMLFormElement)) return;

      const drawer = getCartDrawerComponent();
      if (!drawer?.contains(button)) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      if (button.disabled) return;
      button.disabled = true;

      try {
        await addHotProductToCart(form);
      } catch (error) {
        console.error('[cart-drawer-hot-add]', error);
      } finally {
        button.disabled = false;
      }
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target.closest('.cart-drawer__hot-add-form');
      if (!(form instanceof HTMLFormElement)) return;

      const drawer = getCartDrawerComponent();
      if (!drawer?.contains(form)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
    },
    true
  );
}

function bindCarouselArrows() {
  document.addEventListener('click', (event) => {
    const prevButton = event.target.closest('[data-hot-carousel-prev]');
    const nextButton = event.target.closest('[data-hot-carousel-next]');
    const trigger = prevButton || nextButton;
    if (!trigger) return;

    const drawer = getCartDrawerComponent();
    if (!drawer?.contains(trigger)) return;

    const carouselId = trigger.getAttribute(prevButton ? 'data-hot-carousel-prev' : 'data-hot-carousel-next');
    if (!carouselId) return;

    const carousel = document.getElementById(carouselId);
    if (!carousel) return;

    const firstCard = carousel.querySelector('.cart-drawer__hot-card');
    const cardWidth = firstCard ? firstCard.getBoundingClientRect().width : 220;
    const step = Math.max(cardWidth + 12, 180);
    const direction = prevButton ? -1 : 1;

    carousel.scrollBy({ left: step * direction, behavior: 'smooth' });
  });
}

function init() {
  if (window[INIT_FLAG]) return;
  window[INIT_FLAG] = true;
  bindHotAddButtons();
  bindCarouselArrows();
}

init();
